import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, Check, Copy } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { platformApi } from '../api'
import { toApiProblem } from '@/lib/api/client'
import { problemMessage } from '@/lib/api/problem'
import type { ProvisionRequest, ProvisionResult } from '../types'

const schema = z.object({
  companyName: z.string().min(1, 'Required').max(255),
  slug: z
    .string()
    .min(1, 'Required')
    .max(63)
    .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, 'Lower-case letters, digits and hyphens only'),
  legalEntityName: z.string().min(1, 'Required').max(255),
  legalEntityCode: z.string().min(1, 'Required').max(64),
  countryCode: z.string().length(2, 'Two letters, e.g. AE'),
  currency: z.string().length(3, 'Three letters, e.g. AED'),
  timezone: z.string().min(1, 'Required'),
  adminEmail: z.string().email('Enter a valid email address'),
  adminFullName: z.string().min(1, 'Required').max(200),
  planCode: z.string().min(1, 'Choose a plan'),
  billingEmail: z.string().email('Enter a valid email address').or(z.literal('')),
  contactName: z.string().max(200).optional(),
  contactPhone: z.string().max(40).optional(),
})

type Values = z.infer<typeof schema>

/**
 * Creating a customer.
 *
 * <p>One form, not a wizard. Everything here is needed and none of it depends on anything
 * else, so splitting it into steps would add clicks and hide the shape of what is about to
 * be created.
 *
 * <p>The result screen is the only place the administrator's password is ever shown. It is
 * stored as a hash and nothing can read it back, so the page says so plainly rather than
 * letting an operator assume they can find it again later.
 */
export function PlatformNewTenantPage() {
  const navigate = useNavigate()
  const [result, setResult] = useState<ProvisionResult | null>(null)
  const [copied, setCopied] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)

  const plans = useQuery({ queryKey: ['platform', 'plans'], queryFn: () => platformApi.plans() })

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      countryCode: 'AE',
      currency: 'AED',
      timezone: 'Asia/Dubai',
      legalEntityCode: 'HO',
      billingEmail: '',
    },
  })

  const provision = useMutation({
    mutationFn: (request: ProvisionRequest) => platformApi.provision(request),
    onSuccess: setResult,
    onError: (error: unknown) =>
      setServerError(problemMessage(toApiProblem(error) ?? error, 'The workspace was not created.')),
  })

  if (result) {
    return (
      <div className="mx-auto max-w-lg space-y-4">
        <div className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-6">
          <div className="mb-4 flex items-center gap-2 text-[var(--color-success)]">
            <Check className="size-5" aria-hidden />
            <h1 className="text-lg font-semibold">{result.companyName} is ready</h1>
          </div>

          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-xs text-[var(--color-muted)]">Administrator</dt>
              <dd className="font-medium">{result.adminEmail}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--color-muted)]">Temporary password</dt>
              <dd className="flex items-center gap-2">
                <code className="rounded bg-[var(--color-page)] px-2 py-1 font-mono">
                  {result.temporaryPassword}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void navigator.clipboard.writeText(result.temporaryPassword)
                    setCopied(true)
                  }}
                >
                  <Copy className="me-1 size-3" aria-hidden />
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--color-muted)]">Plan</dt>
              <dd className="font-medium">
                {result.planCode} · {result.status.toLowerCase()}
              </dd>
            </div>
          </dl>

          <p className="mt-5 rounded-[var(--radius-control)] bg-[var(--color-tint-orange)] p-3 text-xs text-[var(--color-warning)]">
            This password is shown once. It is stored only as a hash, so nothing can read it
            back — if it is lost, reset the account instead. The administrator must change it
            at first sign-in.
          </p>

          <div className="mt-5 flex gap-2">
            <Button onClick={() => void navigate(`/platform/tenants/${result.companyId}`)}>
              Open workspace
            </Button>
            <Button variant="outline" onClick={() => void navigate('/platform/tenants')}>
              Back to list
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to="/platform/tenants"
        className="inline-flex items-center gap-1 text-sm text-[var(--color-muted)] hover:underline"
      >
        <ArrowLeft className="size-4" aria-hidden />
        All workspaces
      </Link>

      <div>
        <h1 className="text-xl font-semibold text-[var(--color-heading)]">New workspace</h1>
        <p className="text-sm text-[var(--color-muted)]">
          Creates the company, its first legal entity, the seven standard roles and an
          administrator — in one step, or not at all.
        </p>
      </div>

      {serverError && (
        <div role="alert" className="rounded-[var(--radius-control)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {serverError}
        </div>
      )}

      <form
        onSubmit={(event) =>
          void handleSubmit((values) =>
            provision.mutate({ ...values, billingEmail: values.billingEmail || null }),
          )(event)
        }
        className="space-y-6"
        noValidate
      >
        <Section title="Company">
          <Row>
            <FormField label="Company name" error={errors.companyName?.message}>
              <Input {...register('companyName')} />
            </FormField>
            <FormField
              label="Slug"
              error={errors.slug?.message}
              hint="Permanent. Used in support and in file storage."
            >
              <Input {...register('slug')} placeholder="acme" />
            </FormField>
          </Row>
          <Row>
            <FormField label="Legal entity" error={errors.legalEntityName?.message}>
              <Input {...register('legalEntityName')} />
            </FormField>
            <FormField label="Entity code" error={errors.legalEntityCode?.message}>
              <Input {...register('legalEntityCode')} />
            </FormField>
          </Row>
          <Row>
            <FormField label="Country" error={errors.countryCode?.message} hint="AE loads the UAE statutory pack.">
              <Input {...register('countryCode')} maxLength={2} />
            </FormField>
            <FormField label="Currency" error={errors.currency?.message}>
              <Input {...register('currency')} maxLength={3} />
            </FormField>
            <FormField label="Time zone" error={errors.timezone?.message}>
              <Input {...register('timezone')} />
            </FormField>
          </Row>
        </Section>

        <Section title="First administrator">
          <Row>
            <FormField
              label="Email"
              error={errors.adminEmail?.message}
              hint="One address belongs to one workspace on this installation."
            >
              <Input type="email" {...register('adminEmail')} />
            </FormField>
            <FormField label="Full name" error={errors.adminFullName?.message}>
              <Input {...register('adminFullName')} />
            </FormField>
          </Row>
        </Section>

        <Section title="Commercial">
          <Row>
            <FormField label="Plan" error={errors.planCode?.message}>
              <select
                {...register('planCode')}
                className="h-10 w-full rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-card)] px-3 text-sm"
              >
                <option value="">Choose a plan…</option>
                {(plans.data ?? []).map((plan) => (
                  <option key={plan.code} value={plan.code}>
                    {plan.name}
                    {plan.trialDays > 0 ? ` · ${plan.trialDays}-day trial` : ''}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Billing email" error={errors.billingEmail?.message}>
              <Input type="email" {...register('billingEmail')} />
            </FormField>
          </Row>
          <Row>
            <FormField label="Contact name">
              <Input {...register('contactName')} />
            </FormField>
            <FormField label="Contact phone">
              <Input {...register('contactPhone')} />
            </FormField>
          </Row>
        </Section>

        <Button type="submit" disabled={isSubmitting || provision.isPending}>
          {provision.isPending ? 'Creating…' : 'Create workspace'}
        </Button>
      </form>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-5">
      <legend className="px-2 text-sm font-semibold uppercase tracking-wide text-[var(--color-muted)]">
        {title}
      </legend>
      <div className="space-y-4">{children}</div>
    </fieldset>
  )
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>
}

function FormField({
  label,
  error,
  hint,
  children,
}: {
  label: string
  error?: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && !error && <p className="text-xs text-[var(--color-muted)]">{hint}</p>}
      {error && <p className="text-xs text-[var(--color-danger)]">{error}</p>}
    </div>
  )
}
